import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Newspaper,
  RefreshCw,
  UserMinus,
  Trash2,
  ExternalLink,
  Mail,
  Square,
  ChevronDown,
  Check,
} from 'lucide-react';

const SCAN_LIMIT = 5000;
const TOKEN_KEY = 'authToken';
const SCAN_AGE_KEY = 'subscriptionScanMaxAgeDays';
const SUBS_COLLAPSE_KEY = 'settingsSubscriptionsExpanded';

const SCAN_AGE_OPTIONS = [
  { value: 180, label: '6 Monate' },
  { value: 365, label: '1 Jahr' },
  { value: 730, label: '2 Jahre' },
  { value: 1825, label: '5 Jahre' },
  { value: 0, label: 'Kein Limit' },
];

const readSubsExpanded = () => {
  try {
    const raw = localStorage.getItem(SUBS_COLLAPSE_KEY);
    if (raw == null) return true;
    return raw === '1' || raw === 'true';
  } catch (_) {
    return true;
  }
};

const ScanAgeDropdown = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const rootRef = useRef(null);
  const closeTimerRef = useRef(0);
  const selected = SCAN_AGE_OPTIONS.find((o) => o.value === value) || SCAN_AGE_OPTIONS[2];

  const closeMenu = useCallback(() => {
    if (!open || closing) return;
    setClosing(true);
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 160);
  }, [open, closing]);

  const toggleMenu = () => {
    if (disabled) return;
    if (open && !closing) closeMenu();
    else {
      window.clearTimeout(closeTimerRef.current);
      setClosing(false);
      setOpen(true);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (!rootRef.current?.contains(e.target)) closeMenu();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, closeMenu]);

  useEffect(() => () => window.clearTimeout(closeTimerRef.current), []);

  useEffect(() => {
    if (disabled && open) closeMenu();
  }, [disabled, open, closeMenu]);

  return (
    <div ref={rootRef} className="relative sm:max-w-xs w-full sm:w-56">
      <button
        type="button"
        id="sub-scan-age"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open && !closing}
        onClick={toggleMenu}
        className={`input-field !py-2 !text-sm w-full flex items-center justify-between gap-2 text-left disabled:opacity-50 ${
          open && !closing ? 'ring-2 ring-primary-500 border-primary-500' : ''
        }`}
      >
        <span className="truncate">{selected.label}</span>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 text-neutral-400 dark:text-neutral-500 transition-transform duration-200 ease-out ${
            open && !closing ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open ? (
        <div
          role="listbox"
          aria-labelledby="sub-scan-age"
          className={`absolute left-0 right-0 top-full z-40 mt-1.5 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg shadow-neutral-900/10 dark:shadow-black/40 origin-top ${
            closing ? 'subscription-age-menu-out' : 'subscription-age-menu-in'
          }`}
        >
          <ul className="py-1 max-h-64 overflow-y-auto">
            {SCAN_AGE_OPTIONS.map((opt) => {
              const active = opt.value === value;
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    disabled={disabled}
                    onClick={() => {
                      onChange(opt.value);
                      closeMenu();
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                      active
                        ? 'bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-200'
                        : 'text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                    }`}
                  >
                    <span className="flex-1 truncate">{opt.label}</span>
                    {active ? (
                      <Check className="w-3.5 h-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                    ) : (
                      <span className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const readStoredMaxAgeDays = () => {
  try {
    const raw = localStorage.getItem(SCAN_AGE_KEY);
    if (raw == null || raw === '') return 730;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0) return 730;
    return n;
  } catch (_) {
    return 730;
  }
};

const formatSeen = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return '—';
  }
};

const senderLabel = (sub) => {
  const name = String(sub.fromName || '')
    .trim()
    .replace(/^["'`]+/, '')
    .replace(/["'`]+$/, '')
    .trim();
  const email = String(sub.fromEmail || '')
    .trim()
    .replace(/^["'<\s]+/, '')
    .replace(/["'>\s]+$/, '')
    .trim();
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return { title: name, subtitle: email };
  }
  return { title: name || email || 'Unbekannt', subtitle: name && email ? email : '' };
};

const sortSubscriptionsByLastSeen = (list) =>
  [...(list || [])].sort((a, b) => {
    const ta = a?.lastSeenAt ? new Date(String(a.lastSeenAt).replace(' ', 'T')).getTime() : 0;
    const tb = b?.lastSeenAt ? new Date(String(b.lastSeenAt).replace(' ', 'T')).getTime() : 0;
    const na = Number.isFinite(ta) ? ta : 0;
    const nb = Number.isFinite(tb) ? tb : 0;
    if (nb !== na) return nb - na;
    return String(a?.fromEmail || '').localeCompare(String(b?.fromEmail || ''));
  });

const normalizeSubEmail = (email) =>
  String(email || '')
    .trim()
    .toLowerCase()
    .replace(/^["'<\s]+/, '')
    .replace(/["'>\s]+$/, '')
    .replace(/^mailto:/i, '');

const mergeSubscriptionHit = (prev, hit) => {
  if (!hit?.fromEmail) return prev;
  const key = normalizeSubEmail(hit.fromEmail);
  const idx = prev.findIndex((s) => normalizeSubEmail(s.fromEmail) === key);
  let next;
  if (idx < 0) {
    next = [{ ...hit, fromEmail: key, fromName: String(hit.fromName || '').replace(/^["'`]+|["'`]+$/g, '').trim() }, ...prev];
  } else {
    next = [...prev];
    const cur = next[idx];
    const curTime = cur.lastSeenAt
      ? new Date(String(cur.lastSeenAt).replace(' ', 'T')).getTime()
      : 0;
    const hitTime = hit.lastSeenAt
      ? new Date(String(hit.lastSeenAt).replace(' ', 'T')).getTime()
      : 0;
    const keepSeen = Number.isFinite(curTime) && (!Number.isFinite(hitTime) || curTime > hitTime);
    const cleanName = String(hit.fromName || cur.fromName || '')
      .replace(/^["'`]+/, '')
      .replace(/["'`]+$/, '')
      .trim();
    next[idx] = {
      ...cur,
      ...hit,
      id: hit.id && !String(hit.id).startsWith('tmp-') ? hit.id : cur.id,
      fromEmail: key,
      fromName: cleanName || cur.fromName,
      lastSeenAt: keepSeen ? cur.lastSeenAt : hit.lastSeenAt || cur.lastSeenAt,
      lastSubject: keepSeen ? cur.lastSubject : hit.lastSubject || cur.lastSubject,
      mailCount: Math.max(Number(cur.mailCount) || 1, Number(hit.mailCount) || 1),
      canUnsubscribe: !!(hit.canUnsubscribe || cur.canUnsubscribe),
      unsubscribeHttp: hit.unsubscribeHttp || cur.unsubscribeHttp,
      unsubscribeMailto: hit.unsubscribeMailto || cur.unsubscribeMailto,
      oneClick: !!(hit.oneClick || cur.oneClick),
    };
  }
  return sortSubscriptionsByLastSeen(next);
};

/** SSE-Events aus einem Chunk-Buffer lesen */
function consumeSseBuffer(buffer, onEvent) {
  let rest = buffer;
  while (true) {
    const sep = rest.indexOf('\n\n');
    if (sep < 0) break;
    const raw = rest.slice(0, sep);
    rest = rest.slice(sep + 2);
    let event = 'message';
    const dataLines = [];
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (!dataLines.length) continue;
    try {
      onEvent(event, JSON.parse(dataLines.join('\n')));
    } catch (_) {
      // ignore malformed
    }
  }
  return rest;
}

const SubscriptionSettings = ({ embedded = false }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const [maxAgeDays, setMaxAgeDays] = useState(readStoredMaxAgeDays);
  const [listExpanded, setListExpanded] = useState(() => embedded || readSubsExpanded());
  const [unsubscribeTarget, setUnsubscribeTarget] = useState(null);
  const abortRef = useRef(null);
  const scanningRef = useRef(false);
  const maxAgeDaysRef = useRef(maxAgeDays);

  useEffect(() => {
    maxAgeDaysRef.current = maxAgeDays;
    try {
      localStorage.setItem(SCAN_AGE_KEY, String(maxAgeDays));
    } catch (_) {}
  }, [maxAgeDays]);

  useEffect(() => {
    if (embedded) return;
    try {
      localStorage.setItem(SUBS_COLLAPSE_KEY, listExpanded ? '1' : '0');
    } catch (_) {}
  }, [listExpanded, embedded]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/subscriptions');
      if (res.data?.success) {
        setSubscriptions(sortSubscriptionsByLastSeen(res.data.subscriptions || []));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Abos konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (embedded) {
      setListExpanded(true);
      return;
    }
    if (window.location.hash === '#subscriptions') {
      setListExpanded(true);
      document.getElementById('subscriptions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [embedded]);

  const handleCancelScan = () => {
    if (!scanningRef.current) return;
    abortRef.current?.abort();
  };

  const handleScan = async () => {
    if (scanningRef.current) return;

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    scanningRef.current = true;
    setScanning(true);
    setScanProgress({ scanned: 0, total: SCAN_LIMIT, uniqueSenders: 0, found: 0 });

    let liveScanned = 0;
    let liveUnique = 0;
    let liveFound = 0;

    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch('/api/subscriptions/scan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          limit: SCAN_LIMIT,
          stream: true,
          chunkSize: 200,
          maxAgeDays: maxAgeDaysRef.current,
        }),
        signal: ctrl.signal,
      });

      if (!res.ok) {
        let msg = 'Scan fehlgeschlagen';
        try {
          const errBody = await res.json();
          if (errBody?.error) msg = errBody.error;
        } catch (_) {}
        throw new Error(msg);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('Streaming nicht unterstützt');

      const decoder = new TextDecoder();
      let buffer = '';
      let finalStats = null;
      let cancelled = false;

      const onEvent = (event, data) => {
        if (event === 'progress') {
          liveScanned = data.scanned || 0;
          liveUnique = data.uniqueSenders || 0;
          liveFound = data.found || 0;
          setScanProgress({
            scanned: liveScanned,
            total: data.total || SCAN_LIMIT,
            uniqueSenders: liveUnique,
            found: liveFound,
            upserted: data.upserted,
            upsertErrors: data.upsertErrors,
          });
        } else if (event === 'hit' && data.subscription) {
          setSubscriptions((prev) => mergeSubscriptionHit(prev, data.subscription));
          if (data.scanned != null) {
            liveScanned = data.scanned;
            liveUnique = data.uniqueSenders ?? liveUnique;
            liveFound = data.found ?? liveFound;
            setScanProgress((p) => ({
              ...(p || {}),
              scanned: liveScanned,
              total: data.total || p?.total || SCAN_LIMIT,
              uniqueSenders: liveUnique,
              found: liveFound,
            }));
          }
        } else if (event === 'done') {
          finalStats = data;
          if (Array.isArray(data.subscriptions) && data.subscriptions.length) {
            setSubscriptions(sortSubscriptionsByLastSeen(data.subscriptions));
          }
        } else if (event === 'cancelled') {
          cancelled = true;
          finalStats = data;
        } else if (event === 'error') {
          throw new Error(data?.error || 'Scan fehlgeschlagen');
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = consumeSseBuffer(buffer, onEvent);
      }
      if (buffer.trim()) buffer = consumeSseBuffer(`${buffer}\n\n`, onEvent);

      setLastScan({
        scanned: finalStats?.scanned ?? liveScanned,
        uniqueSenders: finalStats?.uniqueSenders ?? liveUnique,
        found: finalStats?.found ?? liveFound,
        upserted: finalStats?.upserted,
        upsertErrors: finalStats?.upsertErrors,
        skippedOld: finalStats?.skippedOld,
        maxAgeDays: finalStats?.maxAgeDays ?? maxAgeDaysRef.current,
        cancelled,
      });

      if (cancelled) {
        toast('Scan abgebrochen — bisherige Treffer bleiben erhalten');
      } else {
        const shown = finalStats?.uniqueSenders ?? finalStats?.subscriptions?.length ?? liveUnique;
        if (shown > 0) {
          toast.success(`${shown} Newsletter-/Abo-Absender gefunden`);
        } else if ((finalStats?.found || liveFound) > 0) {
          toast.error(
            finalStats?.lastUpsertError
              ? `Treffer erkannt, Speichern fehlgeschlagen: ${finalStats.lastUpsertError}`
              : 'Treffer erkannt, aber Liste ist leer'
          );
        } else {
          toast.success('Scan fertig — keine Abos in den letzten Mails entdeckt');
        }
        try {
          const fresh = await axios.get('/api/subscriptions');
          if (fresh.data?.success && Array.isArray(fresh.data.subscriptions)) {
            setSubscriptions(sortSubscriptionsByLastSeen(fresh.data.subscriptions));
          }
        } catch (_) {}
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        setLastScan({
          scanned: liveScanned,
          uniqueSenders: liveUnique,
          found: liveFound,
          cancelled: true,
        });
        toast('Scan abgebrochen — bisherige Treffer bleiben erhalten');
      } else {
        toast.error(err.message || 'Scan fehlgeschlagen');
      }
    } finally {
      scanningRef.current = false;
      abortRef.current = null;
      setScanning(false);
      setScanProgress(null);
    }
  };

  const requestUnsubscribe = (sub) => {
    if (!sub?.canUnsubscribe) {
      toast.error('Für diesen Absender ist kein Abmelde-Link hinterlegt');
      return;
    }
    setUnsubscribeTarget(sub);
  };

  const confirmUnsubscribe = async () => {
    const sub = unsubscribeTarget;
    if (!sub) return;

    const isTemp = typeof sub.id === 'string' && String(sub.id).startsWith('tmp-');
    if (isTemp) {
      if (sub.unsubscribeHttp) {
        window.open(sub.unsubscribeHttp, '_blank', 'noopener,noreferrer');
        toast.success('Abmelde-Seite geöffnet — bitte dort bestätigen');
        setSubscriptions((prev) => prev.filter((s) => s.id !== sub.id));
        setUnsubscribeTarget(null);
        return;
      }
      toast.error('Bitte erneut scannen, sobald die Speicherung funktioniert');
      setUnsubscribeTarget(null);
      return;
    }

    // Ohne One-Click braucht der Empfänger meist die Bestätigungsseite —
    // Fenster noch im Klick-Handler öffnen (sonst blockiert der Browser).
    let browserOpened = false;
    if (sub.unsubscribeHttp && !sub.oneClick) {
      window.open(sub.unsubscribeHttp, '_blank', 'noopener,noreferrer');
      browserOpened = true;
    }

    try {
      setBusyId(sub.id);
      const res = await axios.post(`/api/subscriptions/${sub.id}/unsubscribe`);
      const openUrl = res.data?.openUrl || null;
      if (openUrl && !browserOpened) {
        window.open(openUrl, '_blank', 'noopener,noreferrer');
        browserOpened = true;
      }
      if (browserOpened || res.data?.result?.needsBrowser) {
        toast.success('Abmelde-Seite geöffnet — bitte dort bestätigen');
      } else if (res.data?.result?.method === 'mailto') {
        toast.success('Abmelde-Mail wurde gesendet');
      } else if (res.data?.result?.method === 'one-click') {
        toast.success('Abmeldung ausgelöst');
      } else {
        toast.success('Abmeldung ausgelöst');
      }
      setSubscriptions((prev) => prev.filter((s) => s.id !== sub.id));
      setUnsubscribeTarget(null);
    } catch (err) {
      const openUrl = err.response?.data?.openUrl || sub.unsubscribeHttp;
      if (openUrl && !browserOpened) {
        window.open(openUrl, '_blank', 'noopener,noreferrer');
        browserOpened = true;
      }
      if (browserOpened) {
        toast.error('Automatische Abmeldung fehlgeschlagen — Link geöffnet');
        setUnsubscribeTarget(null);
      } else {
        toast.error(err.response?.data?.error || 'Abmelden fehlgeschlagen');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (sub) => {
    const isTemp = typeof sub.id === 'string' && String(sub.id).startsWith('tmp-');
    if (isTemp) {
      setSubscriptions((prev) => prev.filter((s) => s.id !== sub.id));
      toast.success('Aus der Liste entfernt');
      return;
    }
    try {
      setBusyId(sub.id);
      await axios.delete(`/api/subscriptions/${sub.id}`);
      setSubscriptions((prev) => prev.filter((s) => s.id !== sub.id));
      toast.success('Aus der Liste entfernt');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Entfernen fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  const progressPct =
    scanProgress?.total > 0
      ? Math.min(100, Math.round((100 * (scanProgress.scanned || 0)) / scanProgress.total))
      : 0;

  const canCollapseList = !embedded && !loading && subscriptions.length > 0;
  const listOpen = embedded || !canCollapseList || listExpanded;

  return (
    <div id="subscriptions" className={embedded ? 'card fade-in' : 'card fade-in scroll-mt-6'}>
      <div className="card-header">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <button
            type="button"
            onClick={() => {
              if (!canCollapseList) return;
              setListExpanded((v) => !v);
            }}
            disabled={!canCollapseList}
            className={`min-w-0 text-left flex-1 ${
              canCollapseList ? 'cursor-pointer' : 'cursor-default'
            }`}
            aria-expanded={listOpen}
            aria-controls="subscriptions-body"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Newspaper className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
              <h3 className="text-base sm:text-lg font-medium text-neutral-900 dark:text-neutral-100 truncate">
                Newsletter & Abos
              </h3>
              {subscriptions.length > 0 ? (
                <span className="text-[11px] tabular-nums px-1.5 py-0.5 rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 flex-shrink-0">
                  {subscriptions.length}
                </span>
              ) : null}
              {canCollapseList ? (
                <ChevronDown
                  className={`w-4 h-4 text-neutral-400 flex-shrink-0 transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                    listOpen ? 'rotate-180' : ''
                  }`}
                />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              <span className="sm:hidden">
                List-Unsubscribe erkennen und Abos abmelden. Scan: bis 5.000 Mails.
              </span>
              <span className="hidden sm:inline">
                Erkennt Absender mit List-Unsubscribe-Header (Newsletter, Listen, Marketing-Mails)
                und lässt dich direkt abmelden. Der Scan prüft bis zu 5.000 der neuesten Inbox-Mails
                im gewählten Zeitraum und lädt Treffer live nach.
              </span>
            </p>
          </button>
          <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
            {scanning ? (
              <button
                type="button"
                onClick={handleCancelScan}
                className="btn-secondary text-sm !py-2.5 sm:!py-2 inline-flex items-center justify-center flex-1 sm:flex-none border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200"
              >
                <Square className="w-3.5 h-3.5 mr-1.5 fill-current" />
                Abbrechen
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setListExpanded(true);
                handleScan();
              }}
              disabled={scanning}
              className="btn-secondary text-sm !py-2.5 sm:!py-2 inline-flex items-center justify-center flex-1 sm:flex-none disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Scannen…' : 'Postfach scannen'}
            </button>
          </div>
        </div>
      </div>

      <div
        id="subscriptions-body"
        className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          listOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden min-h-0">
          <div
            className={`card-body space-y-4 transition-opacity duration-300 ${
              listOpen ? 'opacity-100' : 'opacity-0'
            }`}
          >
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <label className="text-sm text-gray-700 dark:text-gray-300 flex-shrink-0" htmlFor="sub-scan-age">
            Zeitraum
          </label>
          <ScanAgeDropdown
            value={maxAgeDays}
            disabled={scanning}
            onChange={setMaxAgeDays}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Nur Mails aus diesem Zeitraum werden berücksichtigt (Standard: 2 Jahre).
          </p>
        </div>

        {scanning && scanProgress ? (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
              <span>
                {scanProgress.scanned} / {scanProgress.total} Mails · {scanProgress.uniqueSenders}{' '}
                Absender · {scanProgress.found} Treffer
              </span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-primary-500 transition-[width] duration-300 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        ) : null}

        {lastScan && !scanning ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Letzter Scan{lastScan.cancelled ? ' (abgebrochen)' : ''}: {lastScan.scanned} Mails
            geprüft · {lastScan.uniqueSenders} Absender · {lastScan.found} Treffer
            {lastScan.maxAgeDays
              ? ` · Zeitraum ${
                  SCAN_AGE_OPTIONS.find((o) => o.value === lastScan.maxAgeDays)?.label ||
                  `${lastScan.maxAgeDays} Tage`
                }`
              : lastScan.maxAgeDays === 0
                ? ' · kein Zeitraum-Limit'
                : ''}
            {lastScan.skippedOld ? ` · ${lastScan.skippedOld} ältere übersprungen` : ''}
            {lastScan.upserted != null ? ` · ${lastScan.upserted} gespeichert` : ''}
            {lastScan.upsertErrors ? (
              <span className="text-amber-700 dark:text-amber-300">
                {' '}
                · {lastScan.upsertErrors} Speicherfehler
              </span>
            ) : null}
          </p>
        ) : null}

        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Laden…</p>
        ) : subscriptions.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 px-4 py-8 text-center">
            <Mail className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {scanning ? 'Suche läuft…' : 'Noch keine Abos erkannt'}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {scanning
                ? 'Gefundene Newsletter erscheinen hier nach und nach.'
                : 'Klicke auf „Postfach scannen“, um die Inbox auf Newsletter und Abonnements zu prüfen.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {subscriptions.map((sub) => {
              const { title, subtitle } = senderLabel(sub);
              const busy = busyId === sub.id;
              return (
                <li
                  key={sub.id || sub.fromEmail}
                  className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-3 bg-white dark:bg-neutral-900/40"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {title}
                    </p>
                    {subtitle ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
                    ) : null}
                    <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500 truncate">
                      {sub.lastSubject || 'Kein Betreff'}
                      <span className="text-gray-300 dark:text-gray-600"> · </span>
                      zuletzt {formatSeen(sub.lastSeenAt)}
                      {sub.mailCount > 1 ? (
                        <>
                          <span className="text-gray-300 dark:text-gray-600"> · </span>
                          {sub.mailCount}× gesehen
                        </>
                      ) : null}
                      {sub.oneClick ? (
                        <>
                          <span className="text-gray-300 dark:text-gray-600"> · </span>
                          One-Click
                        </>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 w-full sm:w-auto">
                    {sub.unsubscribeHttp ? (
                      <a
                        href={sub.unsubscribeHttp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 sm:p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-950/30 touch-manipulation"
                        title="Abmelde-Link öffnen"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : null}
                    {sub.canUnsubscribe ? (
                      <button
                        type="button"
                        disabled={busy || scanning}
                        onClick={() => requestUnsubscribe(sub)}
                        className="inline-flex flex-1 sm:flex-none items-center justify-center rounded-lg border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-xs font-medium px-2.5 py-2 sm:py-1.5 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-40 touch-manipulation"
                        title="Abmelden"
                      >
                        <UserMinus className="w-3.5 h-3.5 mr-1" />
                        {busy ? '…' : 'Abmelden'}
                      </button>
                    ) : (
                      <span
                        className="inline-flex flex-1 sm:flex-none items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60 text-neutral-500 dark:text-neutral-400 text-xs font-medium px-2.5 py-2 sm:py-1.5 cursor-default"
                        title="In den Mail-Headern war kein List-Unsubscribe-Link"
                      >
                        Kein Link gefunden
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={busy || scanning}
                      onClick={() => handleDismiss(sub)}
                      className="p-2.5 sm:p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40 touch-manipulation"
                      title="Aus Liste entfernen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
          </div>
        </div>
      </div>

      {unsubscribeTarget
        ? createPortal(
            <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
                onClick={() => {
                  if (busyId === unsubscribeTarget.id) return;
                  setUnsubscribeTarget(null);
                }}
              />
              <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
                <div className="px-5 pt-5 pb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 flex items-center justify-center flex-shrink-0">
                      <UserMinus className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                        Newsletter abmelden?
                      </h3>
                      <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                        Möchtest du dich von{' '}
                        <span className="font-medium text-neutral-800 dark:text-neutral-200">
                          „{senderLabel(unsubscribeTarget).title}“
                        </span>{' '}
                        abmelden?
                        {senderLabel(unsubscribeTarget).subtitle ? (
                          <>
                            <br />
                            <span className="text-xs text-neutral-500">
                              {senderLabel(unsubscribeTarget).subtitle}
                            </span>
                          </>
                        ) : null}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
                  <button
                    type="button"
                    disabled={busyId === unsubscribeTarget.id}
                    onClick={confirmUnsubscribe}
                    className="inline-flex items-center justify-center rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60"
                  >
                    {busyId === unsubscribeTarget.id ? 'Abmelden…' : 'Abmelden'}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === unsubscribeTarget.id}
                    onClick={() => setUnsubscribeTarget(null)}
                    className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-60"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default SubscriptionSettings;
